/**
 * Content-Disposition ヘッダーからダウンロード用のファイル名を取り出す。
 *
 * blob URL 経由のダウンロードでは元レスポンスのヘッダーがブラウザに伝わらないため、
 * `<a download>` に渡すファイル名はフロント側でヘッダーから復元する必要がある。
 *
 * バックエンドは日本語ファイル名のため RFC 5987 形式（filename*=UTF-8''...）で送る。
 * 他形式のサーバやプロキシを経由した場合に備え、素の filename= も後方互換として読む。
 */

/** ファイル名として使えない文字を除去する（ヘッダー由来の値を無検証で使わない）。 */
function sanitize(name: string): string | null {
  // パス要素を落として最終要素のみ採用する（../ や絶対パスの持ち込みを防ぐ）
  const base = name.split(/[/\\]/).pop() ?? '';
  // 制御文字（0x00-0x1F, 0x7F）と、Windows でファイル名に使えない文字を除去する。
  // 正規表現に制御文字を直接書くとソース上で不可視になるため、コードポイントで判定する。
  const FORBIDDEN = '<>:"|?*';
  const cleaned = Array.from(base)
    .filter((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      if (code < 0x20 || code === 0x7f) return false;
      return !FORBIDDEN.includes(ch);
    })
    .join('')
    .trim();
  if (!cleaned || cleaned === '.' || cleaned === '..') return null;
  return cleaned;
}

export function parseContentDispositionFilename(
  header: string | undefined | null,
): string | null {
  if (!header) return null;

  // RFC 5987: filename*=UTF-8''%E3%83%AC... （言語タグ部分は空でも可）
  const extended = header.match(/filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i);
  if (extended) {
    try {
      return sanitize(decodeURIComponent(extended[3].trim()));
    } catch {
      // 不正なパーセントエンコーディングは無視し、素の filename= にフォールバックする
    }
  }

  // 後方互換: filename="..." / filename=...
  const plain = header.match(/filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i);
  if (plain) {
    const value = (plain[1] ?? plain[2] ?? '').trim();
    if (value) return sanitize(value);
  }

  return null;
}
